import { useState, useEffect, FormEvent } from "react";
import { useParams, Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  ShoppingCart, Plus, Minus, X, MapPin, Instagram, MessageCircle,
  Package, ArrowLeft, Loader2, Store, Globe, Clock, Ban,
  RefreshCw, ChevronLeft, ChevronRight, Lock
} from "lucide-react";
import { formatCurrency } from "@/lib/currency";
import { formatWhatsAppOrder, whatsappPhone } from "@/lib/whatsapp-order";
import { trackEvent } from "@/lib/analytics";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");
const LOGO = `${import.meta.env.BASE_URL}logo.png`;

function contrastColor(background: string): "#ffffff" | "#0f172a" {
  const match = background.trim().match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
  if (!match) return "#ffffff";
  const hex = match[1].length === 3
    ? match[1].split("").map(char => char + char).join("")
    : match[1];
  const channels = [0, 2, 4].map(index => {
    const value = parseInt(hex.slice(index, index + 2), 16) / 255;
    return value <= 0.04045 ? value / 12.92 : Math.pow((value + 0.055) / 1.055, 2.4);
  });
  const luminance = 0.2126 * channels[0] + 0.7152 * channels[1] + 0.0722 * channels[2];
  const whiteContrast = 1.05 / (luminance + 0.05);
  const darkContrast = (luminance + 0.05) / 0.057;
  return darkContrast >= whiteContrast ? "#0f172a" : "#ffffff";
}

function hexToHsl(hex: string) {
  const match = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex.trim());
  if (!match) return "0 0% 0%";
  const r = parseInt(match[1], 16) / 255;
  const g = parseInt(match[2], 16) / 255;
  const b = parseInt(match[3], 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let h = 0, s = 0, l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r: h = (g - b) / d + (g < b ? 6 : 0); break;
      case g: h = (b - r) / d + 2; break;
      case b: h = (r - g) / d + 4; break;
    }
    h /= 6;
  }
  return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

function generateIdempotencyKey(): string {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  const array = new Uint32Array(4);
  crypto.getRandomValues(array);
  return Array.from(array).map(n => n.toString(16)).join("");
}

interface Product {
  id: number; name: string; description: string | null; price: string;
  imageUrl: string | null; category: string | null; stock: number | null;
  isAvailable: boolean; isService: boolean; shippingWeightKg?: number | null;
  imageUrls?: string[]; availableSizes?: string[];
}
interface StoreData {
  slug: string; displayName: string; tagline: string | null;
  logoUrl: string | null; bannerUrl: string | null; whatsapp: string | null; instagram: string | null;
  address: string | null; primaryColor: string; products: Product[]; categories: string[];
  page: number; pageSize: number; total: number; totalPages: number;
}
interface CartItem { product: Product; qty: number; }

interface FezQuote {
  feeKobo: number;
  baseCostKobo: number;
  vatKobo: number;
  surchargeKobo: number;
  weightKg: number;
  recipientState: string;
  pickUpState: string;
  fingerprint: string;
}

function ProductImage({ product }: { product: Product }) {
  const [failed, setFailed] = useState(false);
  const coverImage = product.imageUrls?.[0] || product.imageUrl;
  if (!coverImage || failed) {
    return (
      <div className="w-full h-full flex flex-col items-center justify-center gap-2 bg-gradient-to-br from-slate-100 to-slate-200 text-slate-500">
        <span className="w-12 h-12 rounded-full bg-white/80 border flex items-center justify-center text-lg font-bold" aria-hidden="true">
          {product.name.trim().charAt(0).toUpperCase() || "?"}
        </span>
        <span className="text-[11px] font-medium">Image unavailable</span>
      </div>
    );
  }
  return (
    <img
      src={coverImage}
      alt={product.name}
      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500 ease-out"
      loading="lazy"
      onError={() => setFailed(true)}
    />
  );
}

interface OrderTracking {
  orderNumber: string;
  trackingToken: string;
  status: string;
  paymentStatus: string;
  totalKobo: number;
  merchandiseAmountKobo: number;
  deliveryFeeKobo: number;
  fulfillmentType: string;
  deliveryZoneName: string | null;
  pickupInstructions: string | null;
  currency: string;
  deliveryAddressSummary: string | null;
  createdAt: string;
  updatedAt: string | null;
  storeName: string;
  items: Array<{ name: string; quantity: number; priceKobo: number; totalKobo: number }>;
  timeline: Array<{ status: string; createdAt: string }>;
  delivery: { status: string; recipientName: string | null; proofText: string | null; deliveredAt: string | null } | null;
  fezDelivery: { bookingStatus: string; orderNo: string | null; status: string | null } | null;
}

function OrderTrackingView({ trackingToken, slug, onBack }: { trackingToken: string, slug: string, onBack: () => void }) {
  const [tracking, setTracking] = useState<OrderTracking | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [fezProgress, setFezProgress] = useState<{ status: string; history: Array<{ status: string; description: string; at: string | null }> } | null>(null);
  const [fezProgressError, setFezProgressError] = useState("");

  const fetchTracking = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    let result: OrderTracking | null = null;
    try {
      const saved = sessionStorage.getItem(`rp_checkout_${slug}`);
      if (saved) {
        const session = JSON.parse(saved);
        if (session.reference && session.trackingToken === trackingToken && !session.verified) {
          await fetch(`${BASE}/api/ecommerce/store/${slug}/order/${trackingToken}/verify`, { method: "POST" }).catch(() => null);
          sessionStorage.setItem(`rp_checkout_${slug}`, JSON.stringify({ ...session, verified: true }));
        }
      }

      const res = await fetch(`${BASE}/api/ecommerce/order/${trackingToken}`);
      if (res.ok) {
        result = await res.json();
        setTracking(result);
        if (result?.fezDelivery?.bookingStatus === "success") {
          try {
            const progressResponse = await fetch(`${BASE}/api/ecommerce/order/${trackingToken}/fez-tracking`);
            if (!progressResponse.ok) throw new Error("Tracking unavailable");
            setFezProgress(await progressResponse.json());
            setFezProgressError("");
          } catch {
            setFezProgressError("Live Fez updates are temporarily unavailable.");
          }
        }
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
    return result;
  };

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout>;
    let isMounted = true;

    const poll = async () => {
      const data = await fetchTracking();
      if (!isMounted) return;

      const terminalStatuses = ["delivered", "cancelled", "failed", "completed"];
      if (data && !terminalStatuses.includes(data.status)) {
        timeoutId = setTimeout(poll, data.paymentStatus === "pending" ? 5000 : 15000);
      }
    };

    poll();

    return () => {
      isMounted = false;
      clearTimeout(timeoutId);
    };
  }, [trackingToken, slug]);

  if (loading) return (
    <div className="flex-1 flex flex-col items-center justify-center py-20 min-h-[60vh]">
      <Loader2 className="w-10 h-10 animate-spin text-primary" />
      <p className="text-muted-foreground mt-4 font-medium animate-pulse">Loading your order...</p>
    </div>
  );

  if (!tracking) return (
    <div className="flex-1 flex flex-col items-center justify-center py-20 text-muted-foreground min-h-[60vh]">
      <Package className="w-16 h-16 mb-4 opacity-50" />
      <p className="text-lg font-medium">Order not found</p>
      <Button variant="outline" onClick={onBack} className="mt-6">Return to Store</Button>
    </div>
  );

  return (
    <div className="max-w-2xl mx-auto w-full px-4 py-8 md:py-12 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <button onClick={onBack} className="text-sm font-semibold flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6 transition-colors">
        <ArrowLeft className="w-4 h-4" /> Back to Store
      </button>

      <div className="bg-card border rounded-3xl shadow-[0_8px_30px_rgba(0,0,0,0.04)] overflow-hidden">
        <div className="p-6 md:p-8 border-b bg-muted/10">
          <div className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm font-semibold text-muted-foreground uppercase tracking-widest mb-1">Order Confirmed</p>
              <h2 className="text-2xl md:text-3xl font-bold tracking-tight">{tracking.orderNumber}</h2>
              <p className="text-sm text-muted-foreground mt-2">{new Date(tracking.createdAt).toLocaleString()}</p>
            </div>
            <div className="flex flex-col items-end gap-2">
              <Badge variant={tracking.paymentStatus === "paid" ? "default" : "secondary"} className={`text-sm px-3 py-1 ${tracking.paymentStatus === "paid" ? "bg-emerald-500 hover:bg-emerald-600 text-white border-transparent" : "font-medium"}`}>
                {tracking.paymentStatus === "paid" ? "Paid" : tracking.paymentStatus}
              </Badge>
              {tracking.paymentStatus === "pending" && (
                <p className="text-xs text-muted-foreground flex items-center gap-1"><Clock className="w-3 h-3" /> Awaiting payment</p>
              )}
            </div>
          </div>
        </div>

        <div className="p-6 md:p-8 space-y-8">
          <div className="space-y-4">
            <h3 className="text-sm font-bold uppercase tracking-widest text-muted-foreground border-b pb-2">Order Summary</h3>
            <div className="space-y-3">
              {tracking.items.map((item, i) => (
                <div key={i} className="flex justify-between items-start text-sm">
                  <span className="text-foreground/90 font-medium">{item.quantity} × {item.name}</span>
                  <span className="font-semibold shrink-0">{formatCurrency(item.totalKobo / 100)}</span>
                </div>
              ))}
              <div className="border-t border-dashed pt-3 mt-2 space-y-2">
                <div className="flex justify-between items-center text-sm font-medium text-muted-foreground">
                  <span>Subtotal</span>
                  <span>{formatCurrency(tracking.merchandiseAmountKobo / 100)}</span>
                </div>
                {tracking.fulfillmentType === "delivery" && tracking.deliveryFeeKobo > 0 && (
                  <div className="flex justify-between items-center text-sm font-medium text-muted-foreground">
                    <span>Delivery Fee</span>
                    <span>{formatCurrency(tracking.deliveryFeeKobo / 100)}</span>
                  </div>
                )}
                <div className="flex justify-between items-center font-bold text-lg pt-1">
                  <span>Total</span>
                  <span className="text-primary">{formatCurrency(tracking.totalKobo / 100)}</span>
                </div>
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h3 className="text-sm font-bold uppercase tracking-widest text-muted-foreground border-b pb-2">
              {tracking.fulfillmentType === "pickup" ? "Store Pickup" : "Delivery Details"}
            </h3>
            <div className="text-sm bg-muted/30 border border-muted/50 p-4 rounded-xl flex flex-col gap-3">
              <div className="flex items-start gap-3">
                {tracking.fulfillmentType === "pickup" ? (
                  <Store className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                ) : (
                  <MapPin className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                )}
                <div className="flex-1">
                  {tracking.fulfillmentType === "pickup" ? (
                    <div className="space-y-1">
                      <p className="font-semibold text-foreground">Pickup from {tracking.storeName}</p>
                      {tracking.pickupInstructions && <p className="text-muted-foreground">{tracking.pickupInstructions}</p>}
                    </div>
                  ) : (
                    <div className="space-y-1">
                      <p className="font-medium text-foreground/80 leading-relaxed">{tracking.deliveryAddressSummary}</p>
                      {tracking.deliveryZoneName && <p className="text-xs text-muted-foreground mt-1">Zone: {tracking.deliveryZoneName}</p>}
                    </div>
                  )}
                </div>
              </div>

              {tracking.delivery && (
                <div className="mt-3 pt-3 border-t border-muted-foreground/20 space-y-2">
                  <div className="flex justify-between items-center">
                    <span className="text-xs uppercase font-bold text-muted-foreground">Delivery Status</span>
                    <Badge variant="secondary" className="capitalize text-xs">{tracking.delivery.status.replace("_", " ")}</Badge>
                  </div>
                  {tracking.delivery.deliveredAt && (
                    <p className="text-xs text-muted-foreground">Delivered at {new Date(tracking.delivery.deliveredAt).toLocaleString()}</p>
                  )}
                  {tracking.delivery.proofText && (
                    <div className="bg-background p-2 rounded border text-xs text-slate-700 italic">
                      Proof: {tracking.delivery.proofText}
                    </div>
                  )}
                  {tracking.delivery.recipientName && (
                    <p className="text-xs font-medium">Received by: {tracking.delivery.recipientName}</p>
                  )}
                </div>
              )}
              {tracking.fezDelivery && (
                <div className="mt-3 pt-3 border-t border-muted-foreground/20 space-y-2">
                  <p className="text-xs uppercase font-bold text-muted-foreground">Fez delivery</p>
                  {tracking.fezDelivery.bookingStatus === "success" ? (
                    <>
                      <p className="text-sm font-medium">{fezProgress?.status ?? tracking.fezDelivery.status ?? "Awaiting first status update"}</p>
                      {tracking.fezDelivery.orderNo && <p className="text-xs text-muted-foreground">Reference: {tracking.fezDelivery.orderNo}</p>}
                      {fezProgress?.history.map((event, index) => (
                        <p key={`${event.at}-${index}`} className="text-xs text-muted-foreground">
                          {event.status}{event.at ? ` · ${event.at}` : ""}{event.description ? ` — ${event.description}` : ""}
                        </p>
                      ))}
                      {fezProgressError && <p className="text-xs text-amber-700">{fezProgressError}</p>}
                    </>
                  ) : (
                    <p className="text-xs text-muted-foreground">Pickup booking is being confirmed.</p>
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between border-b pb-2">
              <h3 className="text-sm font-bold uppercase tracking-widest text-muted-foreground">Order Status</h3>
              <button data-testid="button-refresh-tracking" onClick={() => fetchTracking(true)} disabled={refreshing} className="text-xs font-semibold px-2 py-1 rounded-md flex items-center gap-1.5 text-primary bg-primary/10 hover:bg-primary/20 transition-colors">
                <RefreshCw className={`w-3 h-3 ${refreshing ? 'animate-spin' : ''}`} /> Refresh
              </button>
            </div>
            <div className="space-y-6 ml-2 border-l-2 border-muted/60 pl-6 py-2 relative">
              {tracking.timeline.map((event, i) => {
                const isLast = i === tracking.timeline.length - 1;
                return (
                  <div key={i} className={`relative ${isLast ? 'opacity-100' : 'opacity-60'}`}>
                    <div className={`absolute -left-[31px] top-1 w-3.5 h-3.5 rounded-full ring-4 ring-card ${isLast ? 'bg-primary' : 'bg-muted-foreground'}`} />
                    <p className="text-base font-bold capitalize tracking-tight text-foreground">{event.status.replace(/_/g, ' ')}</p>
                    <p className="text-xs font-medium text-muted-foreground mt-0.5">{new Date(event.createdAt).toLocaleString()}</p>
                  </div>
                )
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Storefront() {
  const params = useParams<{ slug: string }>();
  const slug = params.slug;

  const [store, setStore] = useState<StoreData | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [cart, setCart] = useState<CartItem[]>(() => {
    try { return JSON.parse(localStorage.getItem(`rp_cart_${slug}`) || "[]"); } catch { return []; }
  });
  // A message-only reference for this visit, not an order number in RetailPulse.
  const [whatsappReference] = useState(() => `RP-${generateIdempotencyKey().slice(0, 8).toUpperCase()}`);
  const [cartOpen, setCartOpen] = useState(false);
  const [cartStep, setCartStep] = useState<"cart" | "details">("cart");
  const [step, setStep] = useState<"store" | "tracking">("store");
  const [trackingToken, setTrackingToken] = useState<string | null>(null);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [detailImageIndex, setDetailImageIndex] = useState(0);
  const [touchStartX, setTouchStartX] = useState<number | null>(null);
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [categorySearch, setCategorySearch] = useState("");
  const [productSearch, setProductSearch] = useState("");
  const [productPage, setProductPage] = useState(1);

  const [fulfillmentOptions, setFulfillmentOptions] = useState<any>(null);
  const [fulfillmentType, setFulfillmentType] = useState<"pickup" | "delivery">("delivery");
  const [deliveryProvider, setDeliveryProvider] = useState<"manual" | "fez">("manual");
  const [deliveryZoneId, setDeliveryZoneId] = useState<string>("");
  const [fezRecipientState, setFezRecipientState] = useState("");
  const [fezCity, setFezCity] = useState("");
  const [fezStates, setFezStates] = useState<string[]>([]);
  const [fezStatesLoading, setFezStatesLoading] = useState(false);
  const [fezStatesError, setFezStatesError] = useState("");
  const [fezStatesRetryNonce, setFezStatesRetryNonce] = useState(0);
  const [fezQuote, setFezQuote] = useState<FezQuote | null>(null);
  const [fezQuoteLoading, setFezQuoteLoading] = useState(false);
  const [fezQuoteError, setFezQuoteError] = useState("");
  const [fezQuoteRetryNonce, setFezQuoteRetryNonce] = useState(0);

  useEffect(() => {
    if (!slug) return;
    fetch(`${BASE}/api/ecommerce/store/${slug}/fulfillment-options`)
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (data) {
          setFulfillmentOptions(data);
          if (data.deliveryZones && data.deliveryZones.length > 0) {
            setDeliveryZoneId(String(data.deliveryZones[0].id));
          }
        }
      });
  }, [slug]);

  const [form, setForm] = useState({ customerName: "", customerEmail: "", customerPhone: "", deliveryAddress: "", notes: "" });
  const [placing, setPlacing] = useState(false);
  const [orderError, setOrderError] = useState("");
  const [discountCode, setDiscountCode] = useState("");
  const [discountResult, setDiscountResult] = useState<{ discountAmountKobo?: number, finalTotalKobo?: number, discountAmount?: number, finalTotal?: number } | null>(null);
  const [discountError, setDiscountError] = useState("");
  const [validatingCode, setValidatingCode] = useState(false);

  const [idempotencyKey, setIdempotencyKey] = useState<string>(generateIdempotencyKey());
  const resetIdempotencyKey = () => setIdempotencyKey(generateIdempotencyKey());

  useEffect(() => {
    if (cart.length > 0) localStorage.setItem(`rp_cart_${slug}`, JSON.stringify(cart));
    else localStorage.removeItem(`rp_cart_${slug}`);
  }, [cart, slug]);

  useEffect(() => {
    if (!cartOpen) {
      const timer = setTimeout(() => setCartStep("cart"), 300);
      return () => clearTimeout(timer);
    }
    return undefined;
  }, [cartOpen]);

  useEffect(() => {
    if (!slug) return undefined;
    let active = true;
    let initialRequest = true;
    const refreshStore = async () => {
      try {
        const query = new URLSearchParams({ page: String(productPage), pageSize: "12" });
        if (selectedCategory !== "All") query.set("category", selectedCategory);
        if (productSearch.trim()) query.set("search", productSearch.trim());
        const response = await fetch(`${BASE}/api/ecommerce/store/${slug}?${query}`);
        if (!active) return;
        if (response.status === 404) {
          setNotFound(true);
          return;
        }
        if (!response.ok) throw new Error("Storefront refresh failed");
        const data = await response.json();
        if (!active) return;
        if (productPage > (data.totalPages || 1)) {
          setProductPage(data.totalPages || 1);
          return;
        }
        setStore(data);
        document.title = `${data.displayName} - RetailPulse Storefront`;
      } catch {
        if (active && initialRequest) setNotFound(true);
      } finally {
        if (active) {
          initialRequest = false;
          setLoading(false);
        }
      }
    };
    const debounce = window.setTimeout(() => { void refreshStore(); }, 200);
    // Inventory is canonical in the POS product record. Refresh periodically so
    // an open storefront reflects stock changes made by another session.
    const interval = window.setInterval(() => { void refreshStore(); }, 30_000);
    return () => {
      active = false;
      window.clearTimeout(debounce);
      window.clearInterval(interval);
    };
  }, [slug, productPage, selectedCategory, productSearch]);

  useEffect(() => {
    if (!slug) return undefined;
    const query = new URLSearchParams(window.location.search);
    const reference = query.get("reference") || query.get("trxref");
    const track = query.get("track");

    if (track) {
      setTrackingToken(track);
      setStep("tracking");
      return undefined;
    }

    const saved = sessionStorage.getItem(`rp_checkout_${slug}`);
    if (saved) {
      try {
        const session = JSON.parse(saved);
        if (reference && reference === session.reference) {
          setTrackingToken(session.trackingToken);
          setStep("tracking");
          window.history.replaceState({}, document.title, `${window.location.pathname}?track=${session.trackingToken}`);
        } else if (session.trackingToken) {
          setTrackingToken(session.trackingToken);
          setStep("tracking");
          window.history.replaceState({}, document.title, `${window.location.pathname}?track=${session.trackingToken}`);
        }
      } catch (e) {}
    }
    return undefined;
  }, [slug]);

  const addToCart = (product: Product) => {
    setCart((prev: CartItem[]) => {
      const existing = prev.find(i => i.product.id === product.id);
      const maxStock = product.isService ? Infinity : (product.stock ?? Infinity);
      if (existing) {
        if (existing.qty < maxStock) {
          return prev.map(i => i.product.id === product.id ? { ...i, qty: i.qty + 1 } : i);
        }
        return prev;
      }
      if (maxStock > 0) {
        return [...prev, { product, qty: 1 }];
      }
      return prev;
    });
  };

  const openProductDetails = (product: Product) => {
    setSelectedProduct(product);
    setDetailImageIndex(0);
  };

  const moveDetailImage = (direction: -1 | 1) => {
    if (!selectedProduct) return;
    const imageCount = selectedProduct.imageUrls?.length || (selectedProduct.imageUrl ? 1 : 0);
    if (imageCount < 2) return;
    setDetailImageIndex(current => (current + direction + imageCount) % imageCount);
  };

  const handleDetailTouchStart = (event: React.TouchEvent<HTMLDivElement>) => {
    setTouchStartX(event.changedTouches[0]?.clientX ?? null);
  };

  const handleDetailTouchEnd = (event: React.TouchEvent<HTMLDivElement>) => {
    const endX = event.changedTouches[0]?.clientX;
    if (touchStartX !== null && endX !== undefined) {
      const distance = endX - touchStartX;
      if (Math.abs(distance) > 48) moveDetailImage(distance > 0 ? -1 : 1);
    }
    setTouchStartX(null);
  };

  const removeFromCart = (productId: number) => setCart((prev: CartItem[]) => prev.filter(i => i.product.id !== productId));

  const updateQty = (productId: number, delta: number) => {
    setCart((prev: CartItem[]) => prev.map(i => {
      if (i.product.id === productId) {
        const maxStock = i.product.isService ? Infinity : (i.product.stock ?? Infinity);
        const newQty = Math.max(0, Math.min(i.qty + delta, maxStock));
        return { ...i, qty: newQty };
      }
      return i;
    }).filter(i => i.qty > 0));
  };

  const cartTotalNaira = cart.reduce((s, i) => s + Number(i.product.price) * i.qty, 0);
  const whatsappRecipient = store?.whatsapp ? whatsappPhone(store.whatsapp) : null;
  const whatsappOrderUrl = whatsappRecipient && store && cart.length
    ? `https://wa.me/${whatsappRecipient}?text=${encodeURIComponent(formatWhatsAppOrder(
      store.displayName,
      cart.map(item => ({
        name: item.product.name,
        quantity: item.qty,
        unitPrice: Number(item.product.price),
      })),
      whatsappReference,
    ))}`
    : null;
  const cartCount = cart.reduce((s, i) => s + i.qty, 0);
  const cartHasFezWeights = cart.length > 0 && cart.every(item =>
    Number.isFinite(Number(item.product.shippingWeightKg)) && Number(item.product.shippingWeightKg) > 0
  );
  const canSelectFez = fulfillmentOptions?.fezAvailable === true && cartHasFezWeights;
  const isFezCheckout = fulfillmentType === "delivery" && deliveryProvider === "fez" && canSelectFez;
  const quoteFingerprint = JSON.stringify({
    recipientState: fezRecipientState,
    items: cart.map(item => ({
      productId: item.product.id,
      quantity: item.qty,
      shippingWeightKg: item.product.shippingWeightKg,
    })),
  });
  const canUseFezDestination = !fezStatesLoading && !fezStatesError && fezStates.includes(fezRecipientState);
  const currentFezQuote = canUseFezDestination && fezQuote?.fingerprint === quoteFingerprint ? fezQuote : null;

  useEffect(() => {
    if (deliveryProvider === "fez" && !canSelectFez) {
      setDeliveryProvider("manual");
    }
  }, [deliveryProvider, canSelectFez]);

  useEffect(() => {
    if (!isFezCheckout || !slug) return undefined;
    let active = true;
    setFezStates([]);
    setFezStatesLoading(true);
    setFezStatesError("");
    fetch(`${BASE}/api/ecommerce/store/${slug}/fez-states`)
      .then(async response => {
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.error || "Could not load Fez delivery destinations.");
        if (!Array.isArray(data.states) || !data.states.length ||
            data.states.some((state: unknown) => typeof state !== "string" || !state.trim())) {
          throw new Error("Fez returned an invalid destination list.");
        }
        if (active) {
          setFezStates(data.states);
          setFezRecipientState(current => data.states.includes(current) ? current : "");
        }
      })
      .catch(error => {
        if (active) setFezStatesError(error instanceof Error ? error.message : "Could not load Fez delivery destinations.");
      })
      .finally(() => { if (active) setFezStatesLoading(false); });
    return () => { active = false; };
  }, [isFezCheckout, slug, fezStatesRetryNonce]);

  useEffect(() => {
    if (!isFezCheckout || !canUseFezDestination || !slug) {
      setFezQuote(null);
      setFezQuoteLoading(false);
      setFezQuoteError("");
      return undefined;
    }

    let active = true;
    setFezQuote(null);
    setFezQuoteLoading(true);
    setFezQuoteError("");
    const fetchQuote = async () => {
      try {
        const response = await fetch(`${BASE}/api/ecommerce/store/${slug}/fez-quote`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            recipientState: fezRecipientState,
            items: cart.map(item => ({ productId: item.product.id, quantity: item.qty })),
          }),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.error || "Could not fetch a Fez delivery quote.");
        const feeKobo = Number(data.feeKobo);
        const weightKg = Number(data.weightKg);
        const baseCostKobo = Number(data.baseCostKobo);
        const vatKobo = Number(data.vatKobo);
        const surchargeKobo = Number(data.surchargeKobo);
        if (data.feeKobo == null || data.baseCostKobo == null || data.vatKobo == null || data.surchargeKobo == null ||
            !Number.isSafeInteger(feeKobo) || !Number.isSafeInteger(baseCostKobo) ||
            !Number.isSafeInteger(vatKobo) || !Number.isSafeInteger(surchargeKobo) ||
            baseCostKobo < 0 || vatKobo < 0 || surchargeKobo < 0 ||
            baseCostKobo + vatKobo + surchargeKobo !== feeKobo ||
            !Number.isFinite(weightKg) || weightKg <= 0) {
          throw new Error("Fez returned an invalid delivery quote. Please retry.");
        }
        if (!active) return;
        setFezQuote({
          feeKobo,
          baseCostKobo,
          vatKobo,
          surchargeKobo,
          weightKg,
          recipientState: data.recipientState || fezRecipientState,
          pickUpState: data.pickUpState || "",
          fingerprint: quoteFingerprint,
        });
      } catch (error) {
        if (!active) return;
        setFezQuoteError(error instanceof Error ? error.message : "Could not fetch a Fez delivery quote.");
      } finally {
        if (active) setFezQuoteLoading(false);
      }
    };
    void fetchQuote();
    return () => { active = false; };
  }, [slug, isFezCheckout, canUseFezDestination, fezRecipientState, quoteFingerprint, fezQuoteRetryNonce]);

  const validateDiscountCode = async () => {
    if (!discountCode.trim()) return;
    setValidatingCode(true); setDiscountError(""); setDiscountResult(null);
    try {
      const res = await fetch(`${BASE}/api/ecommerce/store/${slug}/validate-code`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ code: discountCode.trim(), items: cart.map(i => ({ productId: i.product.id, quantity: i.qty })) }),
      });
      const data = await res.json();
      if (!res.ok) { setDiscountError(data.error || "Invalid code"); return; }
      setDiscountResult(data);
    } catch { setDiscountError("Could not validate code. Try again."); }
    finally { setValidatingCode(false); }
  };

  const discountAmountNaira = discountResult
    ? (discountResult.discountAmountKobo != null ? discountResult.discountAmountKobo / 100 : (discountResult.discountAmount || 0))
    : 0;

  const effectiveTotalNaira = discountResult
    ? (discountResult.finalTotalKobo != null ? discountResult.finalTotalKobo / 100 : (discountResult.finalTotal != null ? discountResult.finalTotal : cartTotalNaira))
    : cartTotalNaira;

  const activeZone = fulfillmentOptions?.deliveryZones?.find((z: any) => String(z.id) === deliveryZoneId);
  const deliveryFeeNaira = fulfillmentType !== "delivery"
    ? 0
    : isFezCheckout
      ? (currentFezQuote ? currentFezQuote.feeKobo / 100 : 0)
      : activeZone ? activeZone.rateKobo / 100 : 0;
  const finalTotalNaira = effectiveTotalNaira + deliveryFeeNaira;

  const retryFezQuote = () => {
    setFezQuote(null);
    setFezQuoteError("");
    setOrderError("");
    setFezQuoteRetryNonce(current => current + 1);
  };

  const placeOrder = async (e: FormEvent) => {
    e.preventDefault();
    if (isFezCheckout && !fezCity.trim()) {
      setOrderError("Enter your delivery city or area before placing this order.");
      return;
    }
    if (isFezCheckout && (!fezRecipientState || !currentFezQuote || fezQuoteLoading)) {
      setOrderError("Get a current Fez delivery quote before placing this order.");
      return;
    }
    setPlacing(true); setOrderError("");
    let attempts = 0;

    while (attempts < 5) {
      try {
        const res = await fetch(`${BASE}/api/ecommerce/store/${slug}/order`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Idempotency-Key": idempotencyKey
          },
          body: JSON.stringify({
            ...form,
            items: cart.map(i => ({ productId: i.product.id, quantity: i.qty })),
            discountCode: discountResult ? discountCode.trim() : undefined,
            callbackUrl: `${window.location.origin}${BASE}/shop/${slug}`,
            fulfillmentType,
            deliveryProvider: isFezCheckout ? "fez" : "manual",
            ...(isFezCheckout
              ? { recipientState: fezRecipientState, recipientCity: fezCity.trim(), quotedFeeKobo: currentFezQuote!.feeKobo }
              : fulfillmentType === "delivery" ? { deliveryZoneId } : {}),
          }),
        });

        const data = await res.json().catch(() => ({}));

        if (res.status === 201 || res.status === 200) {
          sessionStorage.setItem(`rp_checkout_${slug}`, JSON.stringify({
            slug, trackingToken: data.trackingToken, reference: data.reference, verified: false
          }));
          setCart([]);
          if (data.authorization_url) {
            window.location.href = data.authorization_url;
            return;
          } else {
            setTrackingToken(data.trackingToken);
            setStep("tracking");
            window.history.replaceState({}, document.title, `${window.location.pathname}?track=${data.trackingToken}`);
            setCartOpen(false);
            setPlacing(false);
            resetIdempotencyKey();
            return;
          }
        } else if (res.status === 202) {
          await new Promise(r => setTimeout(r, 2000));
          attempts++;
        } else if (res.status === 409) {
          const quoteChanged = isFezCheckout && (
            data.quoteChanged === true ||
            data.code === "FEZ_QUOTE_CHANGED" ||
            /quote|delivery fee/i.test(String(data.error || data.message || data.code || ""))
          );
          if (quoteChanged) {
            setFezQuote(null);
            setFezQuoteError("The Fez fee changed before checkout. Retry the quote and review the updated total before paying.");
            setOrderError("Fez delivery quote changed. Retry the quote to continue.");
            setPlacing(false);
            return;
          }
          if (data.terminal) {
            setOrderError(data.error || "Checkout failed");
            resetIdempotencyKey();
            setPlacing(false);
            return;
          }
          const retryAfter = res.headers?.get("Retry-After") || data.retryAfterSeconds || 2;
          await new Promise(r => setTimeout(r, Number(retryAfter) * 1000));
          attempts++;
        } else if (res.status === 400 || res.status === 404) {
          setOrderError(data.error || "Invalid request");
          resetIdempotencyKey();
          setPlacing(false);
          return;
        } else if (res.status === 429 || res.status === 502 || res.status === 503) {
          await new Promise(r => setTimeout(r, 3000));
          attempts++;
        } else {
          setOrderError(data.error || "Failed to place order");
          resetIdempotencyKey();
          setPlacing(false);
          return;
        }
      } catch (err) {
        await new Promise(r => setTimeout(r, 2000));
        attempts++;
      }
    }
    setOrderError("Checkout timed out. Please try again.");
    setPlacing(false);
  };

  const primaryColor = store?.primaryColor || "#6366f1";
  const primaryHsl = hexToHsl(primaryColor);
  const primaryFgHsl = hexToHsl(contrastColor(primaryColor));

  if (loading) return (
    <div className="min-h-[100dvh] flex items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-4">
        <Loader2 className="w-10 h-10 animate-spin text-primary" />
        <p className="text-muted-foreground font-medium animate-pulse">Loading store...</p>
      </div>
    </div>
  );

  if (notFound || !store) return (
    <div className="min-h-[100dvh] flex flex-col items-center justify-center bg-background text-center px-4">
      <div className="w-24 h-24 bg-muted rounded-full flex items-center justify-center mb-6">
        <Store className="w-12 h-12 text-muted-foreground/50" />
      </div>
      <h1 className="text-3xl font-bold text-foreground mb-3">Store not found</h1>
      <p className="text-muted-foreground text-lg mb-8 max-w-md">This store doesn't exist or hasn't been published yet. Check the link and try again.</p>
      <Link href="/shop" className="inline-flex items-center justify-center rounded-full text-sm font-semibold shadow-sm h-11 px-8 bg-primary text-primary-foreground hover:bg-primary/90">
        Browse Marketplace
      </Link>
    </div>
  );

  const categories = ["All", ...(store.categories ?? [])];
  const filteredProducts = store.products;
  const detailImages = selectedProduct
    ? (selectedProduct.imageUrls?.length ? selectedProduct.imageUrls : selectedProduct.imageUrl ? [selectedProduct.imageUrl] : [])
    : [];
  const selectedProductInCart = selectedProduct
    ? cart.find(item => item.product.id === selectedProduct.id)
    : undefined;

  return (
    <div
      style={{
        '--primary': primaryHsl,
        '--primary-foreground': primaryFgHsl,
        '--ring': primaryHsl,
      } as React.CSSProperties}
      className="min-h-[100dvh] bg-slate-50/50 flex flex-col font-sans selection:bg-primary/20 selection:text-primary relative"
    >
      {step === "tracking" && trackingToken ? (
        <OrderTrackingView
          trackingToken={trackingToken}
          slug={slug!}
          onBack={() => {
            sessionStorage.removeItem(`rp_checkout_${slug}`);
            setTrackingToken(null);
            setStep("store");
            window.history.replaceState({}, document.title, window.location.pathname);
          }}
        />
      ) : (
        <>
          <header className="sticky top-0 z-40 bg-white/80 backdrop-blur-md border-b shadow-sm/50">
            <div className="max-w-5xl mx-auto px-4 h-16 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 min-w-0">
                {store.logoUrl ? (
                  <img src={store.logoUrl} alt={store.displayName} className="w-10 h-10 rounded-xl object-cover ring-1 ring-border shadow-sm" />
                ) : (
                  <div className="w-10 h-10 rounded-xl flex items-center justify-center font-bold text-lg shrink-0 shadow-sm bg-primary text-primary-foreground">
                    {store.displayName[0]?.toUpperCase()}
                  </div>
                )}
                <div className="min-w-0">
                  <p className="font-bold text-foreground truncate leading-tight">{store.displayName}</p>
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    <Store className="w-3 h-3" /> Verified Merchant
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Link href="/shop" className="hidden sm:flex items-center gap-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground mr-2 transition-colors">
                  <Globe className="w-4 h-4" /> Marketplace
                </Link>
                <button
                  data-testid="button-cart"
                  onClick={() => setCartOpen(true)}
                  className="relative flex items-center justify-center w-10 h-10 rounded-full bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
                >
                  <ShoppingCart className="w-5 h-5" />
                  {cartCount > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 min-w-[22px] h-[22px] px-1 bg-primary text-primary-foreground rounded-full text-[11px] flex items-center justify-center font-bold ring-2 ring-background animate-in zoom-in">
                      {cartCount}
                    </span>
                  )}
                </button>
              </div>
            </div>
          </header>

          <div
            className="bg-primary text-primary-foreground relative overflow-hidden"
            style={store.bannerUrl ? { backgroundImage: `linear-gradient(90deg, rgba(15,23,42,.78), rgba(15,23,42,.38)), url("${store.bannerUrl}")`, backgroundSize: "cover", backgroundPosition: "center" } : undefined}
          >
            {!store.bannerUrl && <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_top_right,_var(--tw-gradient-stops))] from-white to-transparent mix-blend-overlay"></div>}
            <div className="max-w-5xl mx-auto px-4 py-12 md:py-16 relative z-10">
              <h1 className="text-3xl md:text-5xl font-bold mb-4 tracking-tight" data-testid="text-store-name">{store.displayName}</h1>
              {store.tagline && <p className="text-lg md:text-xl opacity-90 max-w-2xl font-light">{store.tagline}</p>}
              <div className="flex flex-wrap gap-4 mt-8 text-sm font-medium">
                {store.address && (
                  <span className="flex items-center gap-1.5 bg-black/10 px-3 py-1.5 rounded-full backdrop-blur-sm"><MapPin className="w-4 h-4" />{store.address}</span>
                )}
                {whatsappRecipient && (
                  <a href={`https://wa.me/${whatsappRecipient}`} target="_blank" rel="noopener noreferrer" className="flex items-center gap-1.5 bg-black/10 px-3 py-1.5 rounded-full backdrop-blur-sm hover:bg-black/20 transition-colors">
                    <MessageCircle className="w-4 h-4" /> WhatsApp
                  </a>
                )}
                {store.instagram && (
                  <a href={`https://instagram.com/${store.instagram.replace("@", "")}`} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 bg-black/10 px-3 py-1.5 rounded-full backdrop-blur-sm hover:bg-black/20 transition-colors">
                    <Instagram className="w-4 h-4" /> Instagram
                  </a>
                )}
              </div>
            </div>
          </div>

          {categories.length > 1 && (
            <div className="bg-background border-b sticky top-16 z-30 shadow-sm/30">
              <div className="max-w-5xl mx-auto px-4 py-3 space-y-2">
                <Input
                  type="search"
                  aria-label="Search product categories"
                  data-testid="input-search-storefront-categories"
                  placeholder="Search categories…"
                  value={categorySearch}
                  onChange={event => setCategorySearch(event.target.value)}
                  className="h-9 max-w-xs"
                />
                <div className="flex gap-2 overflow-x-auto no-scrollbar scroll-smooth">
                {categories.filter(cat => cat === "All" || cat.toLocaleLowerCase().includes(categorySearch.trim().toLocaleLowerCase())).map(cat => (
                  <button
                    key={cat}
                    data-testid={`button-category-${cat}`}
                    onClick={() => { setSelectedCategory(cat); setProductPage(1); }}
                    className={`shrink-0 px-5 py-2 rounded-full text-sm font-semibold transition-all duration-200 ${
                      selectedCategory === cat
                        ? "bg-primary text-primary-foreground shadow-md"
                        : "bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground"
                    }`}
                  >
                    {cat}
                  </button>
                ))}
                </div>
              </div>
            </div>
          )}

          <main className="max-w-5xl mx-auto px-4 py-8 flex-1 w-full">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
              <Input
                type="search"
                value={productSearch}
                onChange={event => { setProductSearch(event.target.value); setProductPage(1); }}
                aria-label="Search store products"
                placeholder="Search products"
                className="w-full sm:max-w-sm bg-background"
              />
              <p className="text-xs text-muted-foreground" aria-live="polite">{store.total} product{store.total === 1 ? "" : "s"}</p>
            </div>
            {filteredProducts.length === 0 ? (
              <div className="text-center py-20 animate-in fade-in">
                <Package className="w-16 h-16 mx-auto text-muted mb-4" />
                <p className="text-lg font-medium text-foreground">No products available</p>
                <p className="text-muted-foreground text-sm mt-1">Check back later or try a different category.</p>
              </div>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                {filteredProducts.map(product => {
                  const inCart = cart.find(i => i.product.id === product.id);
                  const maxStock = product.isService ? Infinity : (product.stock ?? Infinity);
                  const isOutOfStock = !product.isService && (product.stock === 0);

                  return (
                    <div key={product.id} data-testid={`card-product-${product.id}`} className="group bg-card rounded-2xl border shadow-sm hover:shadow-md transition-all duration-300 overflow-hidden flex flex-col">
                      <button
                        type="button"
                        data-testid={`button-view-product-${product.id}`}
                        aria-label={`View ${product.name} details`}
                        onClick={() => openProductDetails(product)}
                        className="block text-left aspect-[4/3] bg-muted/30 relative overflow-hidden"
                      >
                        <ProductImage product={product} />
                        {isOutOfStock && (
                          <div className="absolute inset-0 bg-background/60 backdrop-blur-[2px] flex items-center justify-center">
                            <Badge variant="secondary" className="font-semibold shadow-sm">Out of Stock</Badge>
                          </div>
                        )}
                      </button>

                      <div className="p-4 flex flex-col flex-1">
                        <button
                          type="button"
                          data-testid={`button-view-product-name-${product.id}`}
                          onClick={() => openProductDetails(product)}
                          className="font-semibold text-sm md:text-base leading-tight mb-1 line-clamp-2 text-left hover:text-primary"
                        >
                          {product.name}
                        </button>
                        {product.category && <p className="text-xs font-medium text-muted-foreground mb-3">{product.category}</p>}

                        <div className="mt-auto flex items-end justify-between gap-2 pt-2">
                          <p className="font-bold text-base md:text-lg text-primary">{formatCurrency(Number(product.price))}</p>

                          {!isOutOfStock && (
                            inCart ? (
                              <div className="flex items-center gap-1.5 bg-muted rounded-full p-1 border">
                                <button data-testid={`button-minus-${product.id}`} onClick={() => updateQty(product.id, -1)} className="w-6 h-6 md:w-7 md:h-7 rounded-full flex items-center justify-center text-foreground hover:bg-background shadow-sm transition-colors"><Minus className="w-3 h-3" /></button>
                                <span data-testid={`text-qty-${product.id}`} className="text-xs md:text-sm font-bold w-4 md:w-6 text-center">{inCart.qty}</span>
                                <button data-testid={`button-plus-${product.id}`} onClick={() => updateQty(product.id, 1)} disabled={inCart.qty >= maxStock} className="w-6 h-6 md:w-7 md:h-7 rounded-full flex items-center justify-center text-primary hover:bg-background shadow-sm transition-colors disabled:opacity-50"><Plus className="w-3 h-3" /></button>
                              </div>
                            ) : (
                              <button data-testid={`button-add-${product.id}`} onClick={() => addToCart(product)} className="w-9 h-9 rounded-full bg-primary text-primary-foreground flex items-center justify-center shrink-0 hover:bg-primary/90 shadow-sm transition-transform active:scale-95">
                                <Plus className="w-4 h-4" />
                              </button>
                            )
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {store.totalPages > 1 && (
              <nav aria-label="Store product pages" className="mt-8 flex items-center justify-center gap-4">
                <Button variant="outline" disabled={productPage <= 1} onClick={() => setProductPage(page => Math.max(1, page - 1))}>Previous</Button>
                <span className="text-sm text-muted-foreground" aria-live="polite">Page {store.page} of {store.totalPages}</span>
                <Button variant="outline" disabled={productPage >= store.totalPages} onClick={() => setProductPage(page => Math.min(store.totalPages, page + 1))}>Next</Button>
              </nav>
            )}
          </main>
        </>
      )}

      <Dialog open={!!selectedProduct} onOpenChange={open => { if (!open) setSelectedProduct(null); }}>
        <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto p-0">
          {selectedProduct && (
            <div className="grid md:grid-cols-[1.1fr_0.9fr]">
              <div className="p-4 sm:p-6 bg-muted/30">
                <div
                  className="relative aspect-square overflow-hidden rounded-xl border bg-background touch-pan-y outline-none focus-visible:ring-2 focus-visible:ring-primary"
                  tabIndex={detailImages.length > 1 ? 0 : undefined}
                  aria-label={detailImages.length > 1 ? "Product photo slider. Use left and right arrow keys to change photos." : undefined}
                  onTouchStart={handleDetailTouchStart}
                  onTouchEnd={handleDetailTouchEnd}
                  onTouchCancel={() => setTouchStartX(null)}
                  onKeyDown={event => {
                    if (event.key === "ArrowLeft") { event.preventDefault(); moveDetailImage(-1); }
                    if (event.key === "ArrowRight") { event.preventDefault(); moveDetailImage(1); }
                  }}
                >
                  {detailImages[detailImageIndex] ? (
                    <img
                      src={detailImages[detailImageIndex]}
                      alt={`${selectedProduct.name} photo ${detailImageIndex + 1}`}
                      className="w-full h-full object-contain"
                      data-testid={`img-product-detail-${selectedProduct.id}`}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                      <Package className="w-12 h-12" />
                    </div>
                  )}
                  {detailImages.length > 1 && (
                    <>
                      <button
                        type="button"
                        data-testid="button-product-image-previous"
                        aria-label="Previous product photo"
                        onClick={() => moveDetailImage(-1)}
                        className="absolute left-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-background/90 shadow flex items-center justify-center hover:bg-background"
                      >
                        <ChevronLeft className="w-5 h-5" />
                      </button>
                      <button
                        type="button"
                        data-testid="button-product-image-next"
                        aria-label="Next product photo"
                        onClick={() => moveDetailImage(1)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-background/90 shadow flex items-center justify-center hover:bg-background"
                      >
                        <ChevronRight className="w-5 h-5" />
                      </button>
                      <span aria-live="polite" className="absolute bottom-3 right-3 rounded-full bg-background/90 px-2.5 py-1 text-xs font-medium">
                        {detailImageIndex + 1} / {detailImages.length}
                      </span>
                    </>
                  )}
                </div>
                {detailImages.length > 1 && (
                  <div className="mt-3 flex gap-2 overflow-x-auto pb-1" aria-label="Product photos">
                    {detailImages.map((imageUrl, index) => (
                      <button
                        type="button"
                        key={`${imageUrl}-${index}`}
                        data-testid={`button-product-image-${index}`}
                        aria-label={`Show product photo ${index + 1}`}
                        aria-pressed={detailImageIndex === index}
                        onClick={() => setDetailImageIndex(index)}
                        className={`h-16 w-16 shrink-0 overflow-hidden rounded-md border-2 ${detailImageIndex === index ? "border-primary" : "border-transparent"}`}
                      >
                        <img src={imageUrl} alt="" className="w-full h-full object-cover" />
                      </button>
                    ))}
                  </div>
                )}
              </div>

              <div className="p-5 sm:p-8 flex flex-col">
                <DialogHeader className="text-left">
                  {selectedProduct.category && <Badge variant="outline" className="w-fit">{selectedProduct.category}</Badge>}
                  <DialogTitle className="text-2xl font-bold">{selectedProduct.name}</DialogTitle>
                  <DialogDescription className="text-sm leading-relaxed">
                    {selectedProduct.description || "Product details and availability"}
                  </DialogDescription>
                </DialogHeader>

                <p className="mt-5 text-3xl font-bold text-primary" data-testid={`text-product-detail-price-${selectedProduct.id}`}>
                  {formatCurrency(Number(selectedProduct.price))}
                </p>

                {!!selectedProduct.availableSizes?.length && (
                  <section className="mt-6" aria-label="Available sizes">
                    <h3 className="text-sm font-semibold">Sizes available</h3>
                    <div className="mt-2 flex flex-wrap gap-2">
                      {selectedProduct.availableSizes.map(size => (
                        <Badge key={size} variant="outline" data-testid={`badge-available-size-${size}`}>{size}</Badge>
                      ))}
                    </div>
                  </section>
                )}

                <div className="mt-auto pt-8">
                  {!selectedProduct.isService && selectedProduct.stock === 0 ? (
                    <Button type="button" className="w-full" disabled>Out of stock</Button>
                  ) : (
                    <Button
                      type="button"
                      className="w-full"
                      data-testid={`button-detail-add-${selectedProduct.id}`}
                      disabled={!!selectedProductInCart && !selectedProduct.isService && selectedProductInCart.qty >= (selectedProduct.stock ?? 0)}
                      onClick={() => addToCart(selectedProduct)}
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      {selectedProductInCart ? `Add another · ${selectedProductInCart.qty} in cart` : "Add to cart"}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {cartOpen && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-background/80 backdrop-blur-sm transition-opacity animate-in fade-in" onClick={() => setCartOpen(false)} />
          <div className="relative w-full max-w-md bg-card h-[100dvh] shadow-2xl border-l flex flex-col animate-in slide-in-from-right duration-300">
            <div className="flex items-center justify-between px-6 py-4 border-b bg-muted/10">
              <h2 className="font-bold text-xl flex items-center gap-2 tracking-tight"><ShoppingCart className="w-5 h-5 text-primary" /> Your Cart</h2>
              <button onClick={() => setCartOpen(false)} className="w-8 h-8 rounded-full flex items-center justify-center hover:bg-muted transition-colors"><X className="w-5 h-5 text-muted-foreground" /></button>
            </div>

            {cartStep === "details" ? (
              <div className="flex-1 overflow-y-auto no-scrollbar">
                <form onSubmit={placeOrder} className="p-6 space-y-6">
                  <button type="button" onClick={() => setCartStep("cart")} className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors -mt-2">
                    <ArrowLeft className="w-4 h-4" /> Back to items
                  </button>

                  <div className="bg-muted/30 border rounded-2xl p-4 text-sm space-y-2">
                    {cart.map(i => (
                      <div key={i.product.id} className="flex justify-between items-start gap-4">
                        <span className="text-foreground/80 leading-tight">{i.qty} × {i.product.name}</span>
                        <span className="font-semibold shrink-0">{formatCurrency(Number(i.product.price) * i.qty)}</span>
                      </div>
                    ))}
                    {discountResult && (
                      <div className="flex justify-between items-center text-emerald-600 font-medium pt-2 border-t mt-2">
                        <span>Discount applied</span>
                        <span>-{formatCurrency(discountAmountNaira)}</span>
                      </div>
                    )}
                    <div className="border-t border-dashed pt-3 mt-2 space-y-2">
                      <div className="flex justify-between items-center text-sm font-medium text-muted-foreground">
                        <span>Subtotal</span>
                        <span>{formatCurrency(effectiveTotalNaira)}</span>
                      </div>
                      {fulfillmentType === "delivery" && (isFezCheckout || deliveryFeeNaira > 0) && (
                        <div className="flex justify-between items-center text-sm font-medium text-muted-foreground">
                          <span>{isFezCheckout ? "Fez delivery fee" : "Delivery Fee"}</span>
                          <span>
                            {isFezCheckout
                              ? (currentFezQuote
                                ? formatCurrency(currentFezQuote.feeKobo / 100)
                                : fezQuoteLoading ? "Fetching live quote…" : "Quote required")
                              : formatCurrency(deliveryFeeNaira)}
                          </span>
                        </div>
                      )}
                      <div className="flex justify-between items-center font-bold text-base pt-1">
                        <span>Total</span>
                        <span className="text-primary">{formatCurrency(finalTotalNaira)}</span>
                      </div>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="text-sm font-semibold block mb-1.5">Discount Code</label>
                      <div className="flex gap-2">
                        <Input
                          placeholder="Enter code"
                          value={discountCode}
                          onChange={e => { setDiscountCode(e.target.value.toUpperCase()); setDiscountResult(null); setDiscountError(""); }}
                          className="uppercase bg-background"
                          disabled={!!discountResult}
                        />
                        {discountResult ? (
                          <Button type="button" variant="outline" onClick={() => { setDiscountResult(null); setDiscountCode(""); setDiscountError(""); }} className="shrink-0">Remove</Button>
                        ) : (
                          <Button type="button" onClick={validateDiscountCode} disabled={validatingCode || !discountCode.trim()} className="shrink-0 w-[88px]">
                            {validatingCode ? <Loader2 className="w-4 h-4 animate-spin" /> : "Apply"}
                          </Button>
                        )}
                      </div>
                      {discountError && <p className="text-xs text-destructive mt-1.5 font-medium">{discountError}</p>}
                    </div>

                    <div className="space-y-4 pt-2">
                      <h3 className="font-semibold text-lg border-b pb-2">Customer Details</h3>
                      <div>
                        <label className="text-sm font-semibold block mb-1.5">Full Name *</label>
                        <Input placeholder="E.g. Amaka Obi" value={form.customerName} onChange={e => setForm(p => ({ ...p, customerName: e.target.value }))} required className="bg-background" />
                      </div>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-sm font-semibold block mb-1.5">Email Address *</label>
                          <Input type="email" placeholder="amaka@example.com" value={form.customerEmail} onChange={e => setForm(p => ({ ...p, customerEmail: e.target.value }))} required className="bg-background" />
                        </div>
                        <div>
                          <label className="text-sm font-semibold block mb-1.5">Phone Number</label>
                          <Input placeholder="08012345678" value={form.customerPhone} onChange={e => setForm(p => ({ ...p, customerPhone: e.target.value }))} className="bg-background" />
                        </div>
                      </div>

                      <div className="pt-2">
                        <h3 className="font-semibold text-lg border-b pb-2 mb-4">Fulfillment Options</h3>

                        <div className="flex p-1 bg-muted/50 rounded-lg mb-4">
                          <button
                            type="button"
                            onClick={() => setFulfillmentType("delivery")}
                            className={`flex-1 text-sm font-medium py-2 rounded-md transition-all ${fulfillmentType === "delivery" ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}
                          >
                            Delivery
                          </button>
                          <button
                            type="button"
                            onClick={() => { setFulfillmentType("pickup"); setDeliveryProvider("manual"); }}
                            className={`flex-1 text-sm font-medium py-2 rounded-md transition-all ${fulfillmentType === "pickup" ? "bg-background shadow-sm text-foreground" : "text-muted-foreground hover:text-foreground"}`}
                          >
                            Store Pickup
                          </button>
                        </div>

                        {fulfillmentType === "pickup" ? (
                          <div className="bg-primary/5 border border-primary/20 rounded-xl p-4 text-sm flex items-start gap-3">
                            <Store className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                            <div>
                              <p className="font-semibold text-primary-foreground/90">Pickup at Store</p>
                              <p className="text-muted-foreground mt-1 mb-2">{fulfillmentOptions?.pickup?.address || "Store location"}</p>
                              <p className="text-xs bg-background border px-2 py-1 rounded inline-block text-slate-600">{fulfillmentOptions?.pickup?.instructions || "Show order reference on arrival"}</p>
                            </div>
                          </div>
                        ) : (
                          <div className="space-y-4">
                            {canSelectFez && (
                              <fieldset className="space-y-2">
                                <legend className="text-sm font-semibold mb-2">Delivery provider</legend>
                                <div className="grid sm:grid-cols-2 gap-2">
                                  <button
                                    type="button"
                                    aria-pressed={deliveryProvider === "manual"}
                                    onClick={() => setDeliveryProvider("manual")}
                                    className={`rounded-xl border p-3 text-left transition-colors ${deliveryProvider === "manual" ? "border-primary bg-primary/5 ring-1 ring-primary" : "bg-background hover:bg-muted/40"}`}
                                  >
                                    <span className="block text-sm font-semibold">Merchant-arranged delivery</span>
                                    <span className="block mt-1 text-xs text-muted-foreground">Your usual rider or delivery process</span>
                                  </button>
                                  <button
                                    type="button"
                                    aria-pressed={deliveryProvider === "fez"}
                                    onClick={() => setDeliveryProvider("fez")}
                                    className={`rounded-xl border p-3 text-left transition-colors ${deliveryProvider === "fez" ? "border-primary bg-primary/5 ring-1 ring-primary" : "bg-background hover:bg-muted/40"}`}
                                  >
                                    <span className="block text-sm font-semibold">Fez delivery</span>
                                    <span className="block mt-1 text-xs text-muted-foreground">Merchant books pickup after payment</span>
                                  </button>
                                </div>
                              </fieldset>
                            )}
                            {fulfillmentOptions?.fezAvailable === true && cart.length > 0 && !cartHasFezWeights && (
                              <p className="rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900">
                                Fez delivery is unavailable for this cart because every product needs a positive shipping weight. You can continue with merchant-arranged delivery.
                              </p>
                            )}
                            {isFezCheckout ? (
                              <div className="space-y-3 rounded-xl border border-primary/20 bg-primary/5 p-4">
                                <div>
                                  <label htmlFor="fez-recipient-state" className="text-sm font-semibold block mb-1.5">Delivery destination state *</label>
                                  <select
                                    id="fez-recipient-state"
                                    data-testid="select-fez-destination-state"
                                    className="w-full bg-background border border-input rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-input"
                                    value={fezRecipientState}
                                    onChange={event => { setFezRecipientState(event.target.value); setFezQuote(null); setOrderError(""); }}
                                    disabled={fezStatesLoading || !!fezStatesError || fezStates.length === 0}
                                    required
                                  >
                                    <option value="">{fezStatesLoading ? "Loading Fez destinations…" : "Select Nigerian state"}</option>
                                    {fezStates.map(state => <option key={state} value={state}>{state === "FCT" ? "Federal Capital Territory (FCT)" : state}</option>)}
                                  </select>
                                </div>
                                {fezStatesError && (
                                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-800" role="alert" data-testid="error-fez-states">
                                    <span>{fezStatesError}</span>
                                    <Button type="button" size="sm" variant="outline" onClick={() => setFezStatesRetryNonce(current => current + 1)} data-testid="button-retry-fez-states">Retry destinations</Button>
                                  </div>
                                )}
                                <div>
                                  <label htmlFor="fez-recipient-city" className="text-sm font-semibold block mb-1.5">Delivery city / area *</label>
                                  <Input
                                    id="fez-recipient-city"
                                    data-testid="input-fez-delivery-city"
                                    placeholder="e.g. Ikeja or Wuse 2"
                                    value={fezCity}
                                    onChange={event => { setFezCity(event.target.value); setOrderError(""); }}
                                    maxLength={100}
                                    required
                                    className="bg-background"
                                  />
                                  <p className="text-xs text-muted-foreground mt-1">This helps the rider find you. Fez quotes by state, not city or area.</p>
                                </div>
                                <p className="text-xs text-muted-foreground">
                                  This is a live Fez delivery quote based on the merchant’s product shipping weights. The quoted fee includes VAT and may be updated by Fez before checkout.
                                </p>
                                {fezQuoteLoading && <p className="text-xs text-muted-foreground flex items-center gap-2" role="status"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Fetching current delivery quote…</p>}
                                {currentFezQuote && !fezQuoteLoading && (
                                  <div className="space-y-1.5 rounded-md bg-background p-3 text-sm" data-testid="summary-fez-delivery-cost">
                                    <div className="flex justify-between gap-2"><span>Delivery · {currentFezQuote.weightKg} kg</span><span>{formatCurrency(currentFezQuote.baseCostKobo / 100)}</span></div>
                                    <div className="flex justify-between gap-2"><span>VAT</span><span>{formatCurrency(currentFezQuote.vatKobo / 100)}</span></div>
                                    {currentFezQuote.surchargeKobo > 0 && <div className="flex justify-between gap-2"><span>Fez surcharge</span><span>{formatCurrency(currentFezQuote.surchargeKobo / 100)}</span></div>}
                                    <div className="flex justify-between gap-2 border-t pt-1.5 font-semibold"><span>Fez delivery total</span><span>{formatCurrency(currentFezQuote.feeKobo / 100)}</span></div>
                                  </div>
                                )}
                                {fezQuoteError && (
                                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 rounded-md border border-red-200 bg-red-50 p-3 text-xs text-red-800" role="alert">
                                    <span>{fezQuoteError}</span>
                                    <Button type="button" size="sm" variant="outline" className="shrink-0" onClick={retryFezQuote}>Retry Fez quote</Button>
                                  </div>
                                )}
                              </div>
                            ) : (
                              <div>
                                <label className="text-sm font-semibold block mb-1.5">Delivery Zone</label>
                                <select
                                  className="w-full bg-background border border-input rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring focus:border-input"
                                  value={deliveryZoneId}
                                  onChange={e => setDeliveryZoneId(e.target.value)}
                                  required
                                >
                                  {(fulfillmentOptions?.deliveryZones || []).map((z: any) => (
                                    <option key={z.id} value={z.id}>{z.name} — {formatCurrency(z.rateKobo / 100)}</option>
                                  ))}
                                  {(fulfillmentOptions?.deliveryZones?.length === 0) && (
                                    <option value="">No delivery zones available</option>
                                  )}
                                </select>
                                <p className="text-xs text-muted-foreground mt-1.5">Delivery fee is the merchant’s rate for this zone.</p>
                              </div>
                            )}

                            <div>
                                <label className="text-sm font-semibold block mb-1.5">{isFezCheckout ? "Street address *" : "Full Delivery Address *"}</label>
                                <Input placeholder={isFezCheckout ? "12 Market Road" : "12 Market Road, Lagos"} value={form.deliveryAddress} onChange={e => setForm(p => ({ ...p, deliveryAddress: e.target.value }))} required className="bg-background" />
                            </div>
                          </div>
                        )}
                      </div>

                      <div>
                        <label className="text-sm font-semibold block mb-1.5 flex justify-between">
                          Notes <span className="text-muted-foreground font-normal">Optional</span>
                        </label>
                        <textarea className="w-full border rounded-md px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary bg-background" rows={3} placeholder="Any special requests..." value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} />
                      </div>
                    </div>
                  </div>

                  {orderError && (
                    <div className="bg-destructive/10 text-destructive text-sm p-3 rounded-lg flex items-start gap-2">
                      <Ban className="w-4 h-4 shrink-0 mt-0.5" />
                      <p>{orderError}</p>
                    </div>
                  )}

                  <div className="pt-4 pb-8">
                    <Button type="submit" data-testid="button-pay" size="lg" className="w-full font-bold text-base h-14 shadow-md" disabled={placing || (fulfillmentType === "delivery" && (isFezCheckout
                      ? !canUseFezDestination || !fezCity.trim() || !currentFezQuote || fezQuoteLoading || !!fezQuoteError
                      : !deliveryZoneId))}>
                      {placing ? <Loader2 className="w-5 h-5 animate-spin" /> : <Lock className="w-4 h-4 mr-2" />}
                      {placing ? "Processing..." : `Pay ${formatCurrency(finalTotalNaira)}`}
                    </Button>
                    {whatsappOrderUrl && (
                      <a href={whatsappOrderUrl} target="_blank" rel="noopener noreferrer"
                        onClick={() => trackEvent("storefront_whatsapp_order_started", { item_count: cartCount })}
                        className="mt-3 flex h-12 w-full items-center justify-center gap-2 rounded-md border border-green-600 font-semibold text-green-700 hover:bg-green-50">
                        <MessageCircle className="h-4 w-4" /> Order via WhatsApp
                      </a>
                    )}
                    {whatsappOrderUrl && <p className="mt-2 text-center text-xs text-muted-foreground">Opens a draft message. Your order is not placed or paid until the seller confirms it.</p>}
                    <p className="text-xs text-center text-muted-foreground mt-3 flex items-center justify-center gap-1">
                      <Lock className="w-3 h-3" /> Secure checkout
                    </p>
                  </div>
                </form>
              </div>
            ) : (
              <div className="flex-1 flex flex-col overflow-hidden">
                <div className="flex-1 overflow-y-auto p-6 space-y-4 no-scrollbar">
                  {cart.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full text-center space-y-4">
                      <div className="w-20 h-20 bg-muted rounded-full flex items-center justify-center">
                        <ShoppingCart className="w-8 h-8 text-muted-foreground" />
                      </div>
                      <div>
                        <p className="text-lg font-semibold">Your cart is empty</p>
                        <p className="text-sm text-muted-foreground mt-1">Looks like you haven't added anything yet.</p>
                      </div>
                      <Button variant="outline" onClick={() => setCartOpen(false)} className="mt-4">Start Shopping</Button>
                    </div>
                  ) : cart.map(item => (
                    <div key={item.product.id} className="flex gap-4 border-b pb-4 last:border-0 last:pb-0">
                      <div className="w-20 h-20 shrink-0 bg-muted rounded-xl overflow-hidden border">
                        {item.product.imageUrl ? (
                          <img src={item.product.imageUrl} alt={item.product.name} className="w-full h-full object-cover" />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center"><Package className="w-6 h-6 text-muted-foreground/40" /></div>
                        )}
                      </div>
                      <div className="flex-1 min-w-0 flex flex-col">
                        <div className="flex justify-between items-start gap-2">
                          <p className="font-semibold text-sm line-clamp-2 leading-snug text-foreground/90">{item.product.name}</p>
                          <button onClick={() => removeFromCart(item.product.id)} className="text-muted-foreground hover:text-destructive transition-colors shrink-0 p-1 -mt-1 -mr-1"><X className="w-4 h-4" /></button>
                        </div>
                        <p className="text-primary font-bold text-sm mt-1">{formatCurrency(Number(item.product.price))}</p>

                        <div className="mt-auto pt-2 flex items-center gap-3">
                          <div className="flex items-center border rounded-full overflow-hidden bg-background">
                            <button onClick={() => updateQty(item.product.id, -1)} className="w-8 h-8 flex items-center justify-center text-foreground hover:bg-muted transition-colors"><Minus className="w-3 h-3" /></button>
                            <span className="w-8 text-center text-sm font-semibold">{item.qty}</span>
                            <button onClick={() => updateQty(item.product.id, 1)} disabled={item.qty >= (item.product.isService ? Infinity : (item.product.stock ?? Infinity))} className="w-8 h-8 flex items-center justify-center text-foreground hover:bg-muted transition-colors disabled:opacity-50"><Plus className="w-3 h-3" /></button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {cart.length > 0 && (
                  <div className="border-t bg-card p-6 shadow-[0_-10px_30px_rgba(0,0,0,0.02)]">
                    <div className="flex justify-between items-end mb-4">
                      <span className="text-muted-foreground font-medium">Subtotal</span>
                      <span className="text-2xl font-bold tracking-tight">{formatCurrency(cartTotalNaira)}</span>
                    </div>
                    <Button data-testid="button-checkout" onClick={() => { setCartStep("details"); setDiscountResult(null); setDiscountCode(""); setDiscountError(""); }} size="lg" className="w-full text-base font-bold h-14 shadow-md group">
                      Proceed to Checkout <ChevronRight className="w-5 h-5 ml-1 group-hover:translate-x-1 transition-transform" />
                    </Button>
                    {whatsappOrderUrl && (
                      <>
                        <a href={whatsappOrderUrl} target="_blank" rel="noopener noreferrer"
                          onClick={() => trackEvent("storefront_whatsapp_order_started", { item_count: cartCount })}
                          className="mt-3 flex h-12 w-full items-center justify-center gap-2 rounded-md border border-green-600 font-semibold text-green-700 hover:bg-green-50">
                          <MessageCircle className="h-4 w-4" /> Order via WhatsApp
                        </a>
                        <p className="mt-2 text-center text-xs text-muted-foreground">Opens a draft message. The seller must confirm your order and payment.</p>
                      </>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {step !== "tracking" && (
        <footer className="border-t bg-card py-8 px-4 text-center mt-auto">
          <Link href="/" className="inline-flex items-center justify-center gap-2 text-sm font-semibold text-muted-foreground hover:text-foreground transition-colors">
            <img src={LOGO} alt="RetailPulse" className="w-5 h-5 rounded grayscale opacity-70" />
            Powered by RetailPulse
          </Link>
        </footer>
      )}
    </div>
  );
}